import React, { useState, useEffect } from 'react';
import { Sidebar } from './Sidebar';
import { Canvas } from './Canvas';
import { ComponentEditor } from './ComponentEditor';
import type { PageData, PageSection, SectionType } from './types';
import { pagesApi } from '../../../services/api';
import { Save, Eye, ArrowLeft } from 'lucide-react';
import { Link, useParams, useNavigate } from 'react-router-dom';

const parseSectionData = (value: unknown) => {
  if (value && typeof value === 'object') return value as Record<string, unknown>;
  if (typeof value === 'string') {
    try {
      return JSON.parse(value) as Record<string, unknown>;
    } catch {
      return {};
    }
  }
  return {};
};

const PageBuilder: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState<PageData>({
    page_title: 'New Page',
    page_slug: '',
    is_published: false,
    sections: []
  });
  const [selectedSectionId, setSelectedSectionId] = useState<string | null>(null);

  const loadPage = async (pageId: string) => {
    setLoading(true);
    try {
      const res = await pagesApi.getById(pageId);
      if (res.success) {
        const raw = res.data || {};
        const normalizedSections = Array.isArray(raw.sections)
          ? raw.sections.map((s: any) => ({
              id: String(s.id ?? crypto.randomUUID()),
              section_type: s.section_type,
              section_data: parseSectionData(s.section_data)
            }))
          : [];
        setPage({
          id: raw.id,
          page_title: String(raw.page_title || raw.title || 'Untitled Page'),
          page_slug: String(raw.page_slug || raw.slug || ''),
          is_published: Boolean(raw.is_published),
          sections: normalizedSections,
          colors_settings: raw.colors_settings
        });
      }
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (id && id !== 'new') {
      loadPage(id);
    }
  }, [id]);

  const addSection = (type: SectionType) => {
    const baseData: any = {
      text: type === 'heading' ? 'New Heading' : type === 'paragraph' ? 'New paragraph text' : undefined,
      level: type === 'heading' ? 'h2' : undefined,
    };
    if (type === 'button') {
      baseData.button_text = 'Click Here';
      baseData.button_url = '#';
      baseData.button_target = '_self';
      baseData.align = 'left';
    }
    if (type === 'divider') {
      baseData.divider_style = 'solid';
      baseData.divider_thickness = 1;
    }
    if (type === 'spacer') {
      baseData.spacer_height = 48;
    }
    if (type === 'list') {
      baseData.list_items = ['First point', 'Second point', 'Third point'];
    }
    if (type === 'quote') {
      baseData.text = 'Add your quote here.';
      baseData.quote_author = 'Author Name';
    }
    if (type === 'two_columns') {
      baseData.columns = [
        { heading: 'Left Column', text: 'Column content' },
        { heading: 'Right Column', text: 'Column content' }
      ];
    }

    const newSection: PageSection = {
      id: crypto.randomUUID(),
      section_type: type,
      section_data: baseData
    };
    setPage(prev => ({
      ...prev,
      sections: [...prev.sections, newSection]
    }));
    setSelectedSectionId(newSection.id);
  };

  const updateSection = (id: string, updates: Partial<PageSection>) => {
    setPage(prev => ({
      ...prev,
      sections: prev.sections.map(s => s.id === id ? { ...s, ...updates } : s)
    }));
  };

  const deleteSection = (id: string) => {
    setPage(prev => ({
      ...prev,
      sections: prev.sections.filter(s => s.id !== id)
    }));
    if (selectedSectionId === id) setSelectedSectionId(null);
  };

  const reorderSection = (fromIndex: number, toIndex: number) => {
    const newSections = [...page.sections];
    const [moved] = newSections.splice(fromIndex, 1);
    newSections.splice(toIndex, 0, moved);
    setPage(prev => ({ ...prev, sections: newSections }));
  };

  const savePage = async () => {
    if (!page.page_slug) {
      alert('Please enter a page slug');
      return;
    }
    setLoading(true);
    try {
      const payload = {
        page_title: page.page_title,
        page_slug: page.page_slug,
        is_published: page.is_published,
        colors_settings: page.colors_settings || {},
        sections: page.sections.map((s, idx) => ({
          id: s.id,
          section_type: s.section_type,
          section_data: s.section_data,
          display_order: idx
        }))
      };
      if (id && id !== 'new') {
        await pagesApi.update(id, payload);
      } else {
        const res = await pagesApi.create(payload);
        if (res.success) {
          navigate(`/dashboard/builder/${res.id || res.data?.id}`);
        }
      }
      alert('Page saved successfully!');
    } catch (e) {
      alert('Error saving page: ' + e);
    }
    setLoading(false);
  };

  const generateSlug = (text: string) => {
    return text
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .trim()
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-');
  };

  const selectedSection = page.sections.find(s => s.id === selectedSectionId) || null;

  return (
    <div className="flex flex-col h-screen bg-[#0b0c15] text-white overflow-hidden">
      {/* Top Bar */}
      <div className="h-16 border-b border-white/10 flex items-center justify-between px-6 bg-[#0f1016]">
        <div className="flex items-center gap-4">
          <Link to="/dashboard" className="p-2 hover:bg-white/5 rounded-full"><ArrowLeft size={20} /></Link>
          <div>
            <input 
              value={page.page_title} 
              onChange={e => {
                const newTitle = e.target.value;
                setPage(p => ({ 
                  ...p, 
                  page_title: newTitle,
                  page_slug: generateSlug(newTitle)
                }));
              }}
              className="bg-transparent border-none text-xl font-bold focus:ring-0 px-0"
              placeholder="Page Title"
            />
            <div className="flex items-center gap-2 text-xs text-gray-400">
              <span>/</span>
              <input 
                value={page.page_slug} 
                onChange={e => setPage(p => ({ ...p, page_slug: e.target.value }))}
                className="bg-transparent border-none text-xs focus:ring-0 px-0 w-32"
                placeholder="slug"
              />
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 mr-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input 
                type="checkbox" 
                checked={page.is_published}
                onChange={e => setPage(p => ({ ...p, is_published: e.target.checked }))}
                className="rounded border-gray-600 bg-gray-700 text-cyan-600 focus:ring-cyan-500"
              />
              <span className="text-sm text-gray-300">Published</span>
            </label>
          </div>
          <button 
            className="flex items-center gap-2 px-4 py-2 bg-gray-800 rounded hover:bg-gray-700 transition"
            onClick={() => window.open(`/page/${page.page_slug}`, '_blank')}
            disabled={!page.page_slug}
          >
            <Eye size={16} /> Preview
          </button>
          <button 
            onClick={savePage} 
            disabled={loading}
            className="flex items-center gap-2 px-6 py-2 bg-cyan-600 rounded hover:bg-cyan-500 transition font-bold"
          >
            <Save size={16} /> {loading ? 'Saving...' : 'Save Page'}
          </button>
        </div>
      </div>

      {/* Main Builder Area */}
      <div className="flex-1 flex overflow-hidden">
        <Sidebar onAdd={addSection} />
        <Canvas 
          sections={page.sections} 
          onSelect={setSelectedSectionId} 
          onDelete={deleteSection}
          onReorder={reorderSection}
          selectedId={selectedSectionId}
        />
        {selectedSection && (
          <ComponentEditor 
            section={selectedSection} 
            onChange={updateSection} 
            onClose={() => setSelectedSectionId(null)} 
          />
        )}
      </div>
    </div>
  );
};

export default PageBuilder;
